import React, { useState, useEffect, useRef, Component } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import { trackEvent } from './utils/telemetry';

// Page modules — extracted in SPD v5.53 Phase D.
import LoginPage           from './pages/LoginPage';
import KnowledgeGraphPage  from './pages/KnowledgeGraphPage';
import KnowledgeAtlasPage  from './pages/KnowledgeAtlasPage';
import KnowledgeControlsPage from './pages/KnowledgeControls';
import EBTAPage             from './pages/EBTAPage';
import EBTAErrorDetailPage  from './pages/EBTAErrorDetailPage';

// Admin surfaces (still under /components/).
import AdminDashboard      from './components/AdminDashboard';
import AdminCodeMaintenance from './components/AdminCodeMaintenance';
import AdminAgents         from './components/AdminAgents';
import AdminScenarios      from './components/AdminScenarios';

// Public surfaces.
import Sitemap             from './components/app/Sitemap';
import ReindexModalPreview from './pages/ReindexModalPreview';
// IJ-270 (2026-08-12) — imports for KgHoverPreview, PreviewTypeaheadMockup,
// PreviewPrimitivesMockup, PreviewPrimitivesMockupV2, PreviewPrimitivesMockupV3
// removed. Preview pages pre-dated Predictive Text FSD v1.8-v1.11 and drifted
// from spec. Source files retained on disk for reference.
// IJ-271 (2026-08-12) — import for ControlCentrePreview removed. Operator
// stated they never touched this preview page. Source file retained on disk.
import SignupPage          from './components/app/SignupPage';

// Route-level admin gate (public-app unhide pass · 2026-02-09).
import RequireAdmin        from './components/RequireAdmin';
import RequireAdminOrDemoAdmin from './components/RequireAdminOrDemoAdmin';
// Route-level operator gate (IJ-054 · Governance Tile viewer).
import RequireOperator     from './components/RequireOperator';

// Auth helpers — moved to utils/ in Phase D.
import { API, getToken, clearToken } from './utils/authClient';


// SPD v5.71 (2026-07-11) — page_view telemetry. Fires once per authenticated
// route change so the admin dashboard's "Beta User Events" tile can count
// unique visitors. Non-authenticated routes (/login, /signup, /) do not emit
// — we do not want to pollute the metric with pre-login redirects. The
// component must live INSIDE BrowserRouter to have access to useLocation.
//
// SPD v5.71-fix (2026-02-11) — Iteration 64 observed the event firing TWICE
// per route change (React StrictMode double-invoke on the dev/preview
// bundle). A useRef last-path guard dedupes so admin dashboard counts stay
// accurate even if StrictMode double-invokes make it into a production
// build.
const PageViewTracker = ({ enabled }) => {
  const location = useLocation();
  const lastPathRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    // Only track pathname (no query string / hash) to avoid leaking any
    // temporary state via the analytics collection.
    const path = (location.pathname || '/').slice(0, 128);
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;
    trackEvent('page_view', { path });
  }, [enabled, location.pathname]);
  return null;
};

// ─── Error Boundary ─────────────────────────────────────────────────────────
// Prevents crashes in KnowledgeGraphPage (or any page) from taking down the
// whole app.  Falls back to a "Something went wrong" screen with a reload
// button.  Logged errors go to `console.error` so they surface in Sentry / any
// error-tracking layer we wire in later.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl p-8 max-w-md text-center border border-gray-800">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-gray-400 mb-6">The application encountered an error. Please refresh the page to continue.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


// ─── App router ─────────────────────────────────────────────────────────────
// Route posture (D1 review · 2026-02-09):
//   /sitemap                → public
//   /knowledge-atlas        → PUBLIC (Pattern A · investor-facing Wiki surface)
//   /knowledge-controls     → admin-only via <RequireAdmin>
//   /signup, /login, /*     → LoginPage catch-all for unauthenticated users
//   /                       → KnowledgeGraphPage (authenticated)
//   /admin/*                → admin surfaces (authenticated)
function App() {
  // SPD v5.77 (2026-02-14) — P0 Auth Bypass fix.
  //
  // Prior behaviour (broken):
  //   • Initial state was `!!getToken()`, so ANY token in localStorage
  //     rendered the authed UI optimistically — even if the token was
  //     revoked server-side. A revoked JWT flashed the authed app while
  //     /auth/verify was in flight.
  //   • The catch-block on /auth/verify silently kept the user logged in
  //     on network errors or non-401 responses. A user could turn off
  //     their internet, reload, and stay in the authed app.
  //
  // New behaviour (fail-CLOSED):
  //   • Initial state is `null` (loading). Routes are not rendered until
  //     verify resolves. No optimistic auth UI.
  //   • On ANY verify failure — 401, network error, 5xx, JSON parse —
  //     we clear the token, wipe the walkthrough-seen flag from both
  //     sessionStorage and localStorage, and drop the user at Walkthrough
  //     Step 1 of 2 (per operator directive).
  //   • Backend defence: the `/exit-banner/fire` route already writes to
  //     both the in-memory `_revoked_users` set and the persistent
  //     `v3_exit_event` ledger. The server startup hook re-hydrates
  //     `_revoked_users` from the ledger so revocations survive restarts.
  //
  // Beacon on tab close: intentionally NOT wired per operator directive
  // ("Only explicit Exit — softer, allows accidental refresh"). The
  // existing Exit Button already fires /exit-banner/fire which handles
  // revocation.
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [username, setUsername] = useState('');

  // SPD v5.78 (2026-02-14) — cross-user state leak fix (P0 hotfix).
  //
  // Symptom: on the same browser, a new signup (e.g. "Test4") landed on
  // the KG page already showing "View Knowledge Graph Demo again" and
  // was counted as having completed the Demo action. Root cause: several
  // UX-state keys — most importantly `si_demo_viewed` — were written to
  // localStorage under a GLOBAL key, not scoped to the username. Once
  // any user on the browser viewed the demo, every subsequent new user
  // inherited that flag.
  //
  // Fix: wipe the per-user UX-state keys on every session boundary
  // (login, logout, fail-closed, and on username-change during verify).
  // Global preference keys (quiet_mode, hidden_tooltips) are left alone
  // — those are per-browser preferences by design.
  //
  // Note on `si_walkthrough_seen`: already wiped on logout and fail-
  // closed (see failClosed / handleLogout below) so it doesn't leak
  // across users either.
  const resetPerUserUXState = () => {
    try { localStorage.removeItem('si_demo_viewed'); } catch { /* noop */ }
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      // No token → unauthenticated session. Also wipe any leftover
      // per-user UX state so a user who signs up in the next moment
      // doesn't inherit the previous user's demo-viewed / walkthrough
      // flags. See SPD v5.78 cross-user state leak fix.
      resetPerUserUXState();
      try { localStorage.removeItem('si_current_user'); } catch { /* noop */ }
      setIsAuthenticated(false);
      return;
    }
    const failClosed = () => {
      clearToken();
      // Force Walkthrough Step 1 of 2 on the next render — operator
      // requirement so a fail-closed user never lands on a bare "Step 2
      // of 2" login form with no context.
      try { sessionStorage.removeItem('si_walkthrough_seen'); } catch { /* noop */ }
      try { localStorage.removeItem('si_walkthrough_seen'); } catch { /* noop */ }
      resetPerUserUXState();
      try { localStorage.removeItem('si_current_user'); } catch { /* noop */ }
      setIsAuthenticated(false);
      setUsername('');
    };
    fetch(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) {
          // Every non-2xx (including 401 revoked, 403 trial exited, 5xx)
          // is treated as untrusted. Fail-CLOSED.
          failClosed();
          return null;
        }
        return res.json();
      })
      .then(data => {
        // `null` here means the previous .then already fail-closed on a
        // non-2xx response — nothing more to do.
        if (data === null) return;
        if (!data || !data.username) {
          // Defensive: 200 with malformed payload should also fail-CLOSED
          // rather than leave the loading gate open indefinitely.
          failClosed();
          return;
        }
        // Cross-user state leak fix. If the verified username differs
        // from the last one this browser knew about, wipe per-user UX
        // state so User B never inherits User A's demo/counter flags.
        try {
          const lastUser = localStorage.getItem('si_current_user');
          if (lastUser !== data.username) {
            resetPerUserUXState();
            localStorage.setItem('si_current_user', data.username);
          }
        } catch { /* noop */ }
        setIsAuthenticated(true);
        setUsername(data.username);
      })
      .catch(() => {
        // Network error, DNS failure, CORS block, JSON parse error, etc.
        // Fail-CLOSED so a user can't stay in the app by dropping their
        // connection.
        failClosed();
      });
  }, []);

  const handleLogin = (user) => {
    // Wipe any leftover per-user UX state from a previous user on this
    // browser BEFORE we hand control to the authed routes. This is the
    // primary defence against the cross-user demo-viewed leak.
    resetPerUserUXState();
    try { localStorage.setItem('si_current_user', user); } catch { /* noop */ }
    setIsAuthenticated(true);
    setUsername(user);
  };
  const handleLogout = () => {
    clearToken();
    // SPD v5.72 (2026-02-11) — Iteration 66 fix. Prior behaviour left
    // `si_walkthrough_seen` set after logout, so a returning operator saw
    // only the standalone login form labelled "Step 2 of 2" with no
    // context of Step 1. Clearing the flag on every logout restores the
    // full walkthrough on the next visit so users always land on Step 1
    // first. Beta-only concern; harmless in enterprise mode where the
    // walkthrough is feature-flagged off.
    try { localStorage.removeItem('si_walkthrough_seen'); } catch { /* noop */ }
    resetPerUserUXState();
    try { localStorage.removeItem('si_current_user'); } catch { /* noop */ }
    setIsAuthenticated(false);
    setUsername('');
  };

  // Sub-processor disclosure modal was removed for Beta (Feb 11, 2026, user
  // request) — surfacing specific third-party providers (Anthropic, DuckDuckGo)
  // pre-launch confused trial users and constrained provider-swap optionality.
  // To be reintroduced at full Launch with a finalised sub-processor list.

  // SPD v5.77 — while auth is loading, render a bare-minimum screen so we
  // never optimistically render the authed UI to a revoked user. The public
  // /sitemap and /knowledge-atlas routes are unaffected because they don't
  // consult `isAuthenticated`.
  const authLoading = isAuthenticated === null;
  return (
    <div className="App">
      <BrowserRouter>
        <PageViewTracker enabled={isAuthenticated === true} />
        <Routes>
          {/* Public — no auth required */}
          <Route path="/sitemap" element={<Sitemap />} />
          {/* Dev-only preview route (2026-02-14) for operator copy review of
              ReindexPromptModal in each of its states. Not linked from the
              app; delete or gate behind NODE_ENV after copy sign-off. */}
          <Route path="/preview/reindex-modal" element={<ReindexModalPreview />} />
          {/* IJ-270 (2026-08-12) — routes for /preview/kg-hover,
              /preview/typeahead-mockup, /preview/primitives-mockup,
              /preview/primitives-mockup-v2, /preview/primitives-mockup-v3
              removed. Pre-dated Predictive Text FSD v1.8-v1.11.
              IJ-271 (2026-08-12) — route for /preview/control-centre removed
              (operator: 'I never touched'). All six URLs now hit the
              catch-all Navigate to `/`. */}
          {/* KC (Cockpit) — admin-only per operator directive (2026-02-09).
              URL remains reachable but only authenticated admin users
              can view the page; non-admins bounce to `/`.

              IJ-259 (2026-08-05) — Wrapper swapped from <RequireAdmin>
              to <RequireAdminOrDemoAdmin> so client-demo accounts flagged
              is_demo_admin=true in the beta_users collection can access
              this route without unlocking any other admin surface. Every
              other admin-only route below still uses the stricter
              <RequireAdmin> wrapper. */}
          <Route
            path="/knowledge-controls"
            element={<RequireAdminOrDemoAdmin><KnowledgeControlsPage /></RequireAdminOrDemoAdmin>}
          />

          {/* Auth-gated routes — held behind a loading gate while
              /api/auth/verify is in flight so a stale/revoked JWT can
              never render the authed UI. */}
          {authLoading ? (
            <Route
              path="*"
              element={
                <div
                  data-testid="app-auth-loading"
                  className="min-h-screen w-full flex items-center justify-center bg-black"
                >
                  <div className="text-cyan-400/70 text-sm tracking-wide">
                    Verifying your session…
                  </div>
                </div>
              }
            />
          ) : !isAuthenticated ? (
            <>
              {/* Public signup landing (Phase 2 v5.14) — destination for the
                  search.inc WP portal CTA. Signup-only experience; users with
                  an existing account click through to /login. */}
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
              {/* Catch-all unauthenticated → unified login/signup form at /. */}
              <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
            </>
          ) : (
            <>
              <Route path="/" element={<KnowledgeGraphPage onLogout={handleLogout} />} />
              {/* GB-018 remediation (IJ-055, 2026-07-21). Knowledge Atlas
                  is authenticated-only. The prior "public per D1 review ·
                  Pattern A" comment was a fabricated post-hoc justification
                  written by a prior E1 (commit ad1de347, 2026-07-09) with
                  ZERO backing IJ entry; operator confirmed the wiki was
                  never intended to be public. Route now sits inside the
                  authed branch alongside KG and KC. */}
              <Route path="/knowledge-atlas" element={<KnowledgeAtlasPage />} />
              {/* SPD v5.76 (2026-07-12) — D2 Judge-gap sweep.
                  Previously these four /admin/* routes were only
                  `isAuthenticated`-gated, so any beta user could
                  navigate directly to them. The Security Judge caught
                  this via unguarded admin-endpoint calls inside
                  `AdminScenarios.jsx`. Wrapped all four in
                  `<RequireAdmin>` for parity with the `/knowledge-controls`
                  route above. Non-admin visitors bounce to `/`. */}
              <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
              <Route path="/admin/code-maintenance" element={<RequireAdmin><AdminCodeMaintenance /></RequireAdmin>} />
              <Route path="/admin/ebta" element={<RequireAdmin><EBTAPage /></RequireAdmin>} />
              <Route path="/admin/ebta/error/:id" element={<RequireAdmin><EBTAErrorDetailPage /></RequireAdmin>} />
              <Route path="/admin/agents" element={<RequireAdmin><AdminAgents /></RequireAdmin>} />
              <Route path="/admin/scenarios" element={<RequireAdmin><AdminScenarios /></RequireAdmin>} />
              {/* IJ-054 (2026-02) — Operator-only Governance Tile viewer.
                  Stricter than RequireAdmin: only the env-admin identity
                  (role=admin AND username=AUTH_USERNAME) passes the gate. */}
              {/* IJ-434 · stale mockup retired · redirect to the live tile. */}
              <Route path="/admin/governance-tile" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}


// ─── Root wrap ──────────────────────────────────────────────────────────────
function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
